import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import { Autocomplete, Card, CardContent, Chip, Stack, TextField, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import { InventoryVersion } from '../../hooks/useInventory';

const ContentTagsCard = ({
    selectedVersion,
    tabIndex,
    tagSuggestions,
    handleAddTag,
    handleRemoveTag,
}: {
    selectedVersion: InventoryVersion;
    tabIndex: number;
    /** Existing tags across all templates, offered as suggestions; new tags can still be typed */
    tagSuggestions?: string[];
    handleAddTag: (index: number, newTag: string) => void;
    handleRemoveTag: (index: number, tagIndex: number) => void;
}) => {
    const [inputValue, setInputValue] = useState('');

    const currentTags = useMemo(
        () => new Set((selectedVersion.tags ?? []).map(t => t.tagText.trim().toLowerCase())),
        [selectedVersion.tags],
    );

    const options = useMemo(
        () => (tagSuggestions ?? []).filter(tag => !currentTags.has(tag.trim().toLowerCase())),
        [tagSuggestions, currentTags],
    );

    const addTag = (newTag: string | null) => {
        const tag = (newTag ?? '').trim();

        if (!tag || currentTags.has(tag.toLowerCase())) {
            setInputValue('');
            return;
        }

        handleAddTag(tabIndex, tag);
        setInputValue('');
    };

    return (
        <Card sx={{ height: '100%' }}>
            <CardContent>
                {/* Header */}
                <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                    <LocalOfferIcon fontSize="small" />
                    <Typography fontWeight={600}>Content Tags</Typography>
                    <Chip size="small" label={selectedVersion.tags?.length ?? 0} sx={{ ml: 1 }} />
                </Stack>

                {/* Body */}
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {(selectedVersion.tags ?? []).map((t, i) => (
                        <Chip key={`${t.tagText}-${i}`} label={t.tagText} onDelete={() => handleRemoveTag(tabIndex, i)} sx={{ mb: 1 }} />
                    ))}
                </Stack>

                <Autocomplete
                    freeSolo
                    size="small"
                    options={options}
                    value={null}
                    inputValue={inputValue}
                    onInputChange={(_, value) => setInputValue(value)}
                    onChange={(_, value) => addTag(value)}
                    clearOnBlur
                    renderInput={params => <TextField {...params} placeholder="Add a tag - pick an existing one or type a new one..." />}
                    sx={{ mt: 1 }}
                />
            </CardContent>
        </Card>
    );
};

export default ContentTagsCard;
